import {
  dataUrlToInline,
  fileToGrayscaleDataUrl,
  loadImage,
} from './generation';
import { areaDims } from './lib/area-geometry';
import { pickOne } from './lib/random';
import { makeZip, type ZipEntry } from './lib/zip';
import { isChoice, isPhoto, state } from './state';
import type {
  AnswerPrompt,
  Area,
  BaseImage,
  ChoiceQuestion,
  ExpertMode,
  ExpertQuestionConfig,
  PhotoQuestion,
  Question,
  Target,
} from './types';
import { fetchRandomPortraitFile } from './ui/portrait-picker';

const MAX_ATTEMPTS = 4;

export type ExpertStatus =
  | 'queued'
  | 'running'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'error';

export type AreaReviewStatus =
  | 'queued'
  | 'running'
  | 'awaiting'
  | 'approved'
  | 'rejected';

type AreaDecision = 'approve' | 'regen' | 'abort';

export interface ExpertSelectionChip {
  qid: string;
  questionTitle: string;
  optionLabel: string | null;
  shortLabel: string | null;
  mode: ExpertMode;
  colorIndex: number | 'photo';
}

export interface ExpertResult {
  id: string;
  fileName: string;
  permutationIndex: number;
  copyIndex: number;
  status: ExpertStatus;
  dataUrl?: string;
  mimeType?: string;
  pngBytes?: Uint8Array;
  errorMessage?: string;
  selections: ExpertSelectionChip[];
  perAreaPrompts: string[];
  // Pinned permute selections: qid → option index
  permutePins: Record<string, number>;
  /** Per-question alt index to use when altCoverageMode != 'random'. Empty otherwise. */
  altIdxByQid: Record<string, number>;
  startedAt?: number;
  durationMs?: number;
  attempts: number;
  areasDone: number;
  areasTotal: number;
  areaStatuses: AreaReviewStatus[];
  currentAreaIdx: number | null;
  /** Internal: resolver used by runOne to await per-area decisions. Not serializable. */
  __areaDecider?: (d: AreaDecision) => void;
}

interface Snapshot {
  questions: Question[];
  questionOrder: string[];
  targets: Target[];
  areas: Area[];
  baseImage: BaseImage;
  openingPrompt: string;
  closingPrompt: string;
  targetTemplate: string;
  expertConfig: Map<string, ExpertQuestionConfig>;
  permutationMode: 'permute' | 'cover';
  altCoverageMode: 'random' | 'simple' | 'smart';
  iterationsPerPermutation: number;
  concurrency: number;
  reviewPerArea: boolean;
  rotateCursorByQid: Map<string, number>;
}

export interface ExpertSession {
  running: boolean;
  results: ExpertResult[];
  snapshot: Snapshot | null;
  startedAt?: number;
}

export const expertSession: ExpertSession = {
  running: false,
  results: [],
  snapshot: null,
};

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeExpert(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

function notify(): void {
  for (const l of listeners) l();
}

export function resetExpertSession(): void {
  expertSession.running = false;
  expertSession.results = [];
  expertSession.snapshot = null;
  notify();
}

export interface ExpertPreview {
  permutationCount: number;
  totalIterations: number;
  permuteQids: string[];
  poolSizes: number[];
  issues: string[];
}

function previewOptionDepth(q: ChoiceQuestion, optIdx: number): number {
  const opt = q.options[optIdx];
  if (!opt || !opt.injects || opt.prompts.length === 0) return 1;
  let max = 1;
  for (const g of opt.prompts) max = Math.max(max, g.alternatives.length);
  return Math.max(1, max);
}

function previewSmartRowCount(): number {
  let rows = 0;
  for (const q of state.questions) {
    const cfg = state.expertConfig.get(q.id);
    if (!cfg || cfg.mode !== 'permute' || !isChoice(q)) continue;
    const pool = cfg.options.map((o, i) => (o.inPool ? i : -1)).filter((i) => i >= 0);
    if (pool.length === 0) continue;
    const qRows = pool.reduce((sum, idx) => sum + previewOptionDepth(q, idx), 0);
    rows = Math.max(rows, qRows);
  }
  return Math.max(1, rows);
}

function previewSimpleRowCount(): number {
  // Enumerate basic permutations the same way buildSchedule will, then sum each row's depth.
  const permuteQs: Array<{ q: ChoiceQuestion; pool: number[] }> = [];
  for (const q of state.questions) {
    const cfg = state.expertConfig.get(q.id);
    if (!cfg || cfg.mode !== 'permute' || !isChoice(q)) continue;
    const pool = cfg.options.map((o, i) => (o.inPool ? i : -1)).filter((i) => i >= 0);
    if (pool.length > 0) permuteQs.push({ q, pool });
  }

  const pinsList: Array<Record<string, number>> = [];
  if (permuteQs.length === 0) {
    pinsList.push({});
  } else if (state.expertPermutationMode === 'cover') {
    const rows = Math.max(...permuteQs.map((p) => p.pool.length));
    for (let r = 0; r < rows; r++) {
      const pins: Record<string, number> = {};
      for (const { q, pool } of permuteQs) pins[q.id] = pool[r % pool.length];
      pinsList.push(pins);
    }
  } else {
    const walk = (i: number, acc: Record<string, number>) => {
      if (i === permuteQs.length) {
        pinsList.push({ ...acc });
        return;
      }
      const { q, pool } = permuteQs[i];
      for (const idx of pool) {
        acc[q.id] = idx;
        walk(i + 1, acc);
      }
    };
    walk(0, {});
  }

  let total = 0;
  for (const pins of pinsList) {
    let depth = 1;
    for (const q of state.questions) {
      const cfg = state.expertConfig.get(q.id);
      if (!cfg) continue;
      if (isChoice(q)) {
        let idx = pins[q.id];
        if (idx === undefined && cfg.mode === 'fixed') {
          const pool = cfg.options.map((o, i) => (o.inPool ? i : -1)).filter((i) => i >= 0);
          idx = pool[0];
        }
        if (idx === undefined) continue;
        depth = Math.max(depth, previewOptionDepth(q, idx));
      } else {
        for (const g of q.prompts) depth = Math.max(depth, g.alternatives.length);
      }
    }
    total += depth;
  }
  return Math.max(1, total);
}

export function computeExpertPreview(): ExpertPreview {
  const issues: string[] = [];
  const permuteQids: string[] = [];
  const poolSizes: number[] = [];
  const mode = state.expertPermutationMode;
  let permutationCount = mode === 'cover' ? 0 : 1;

  for (const q of state.questions) {
    const cfg = state.expertConfig.get(q.id);
    if (!cfg) continue;
    if (!isChoice(q)) continue;

    const poolIndices = cfg.options
      .map((o, i) => (o.inPool ? i : -1))
      .filter((i) => i >= 0);

    if (cfg.mode === 'permute') {
      if (poolIndices.length === 0) {
        issues.push(`Question "${q.title}" is set to Permute but has no options in its pool.`);
        continue;
      }
      permuteQids.push(q.id);
      poolSizes.push(poolIndices.length);
      if (mode === 'cover') {
        permutationCount = Math.max(permutationCount, poolIndices.length);
      } else {
        permutationCount *= poolIndices.length;
      }
    } else if (cfg.mode !== 'fixed' && poolIndices.length === 0) {
      issues.push(`Question "${q.title}" is set to ${cfg.mode} but has no options in its pool.`);
    } else if (cfg.mode === 'fixed' && poolIndices.length === 0) {
      issues.push(`Question "${q.title}" (Fixed) has no option selected.`);
    }
  }

  if (permuteQids.length === 0) permutationCount = 1;

  if (!state.baseImage) issues.push('No base image loaded.');
  if (state.areas.length === 0) issues.push('No areas defined.');

  const altMode = state.expertAltCoverageMode;
  const N = Math.max(1, state.expertIterationsPerPermutation | 0);
  let scheduleRows: number;
  if (altMode === 'smart') {
    scheduleRows = previewSmartRowCount();
  } else if (altMode === 'simple') {
    scheduleRows = previewSimpleRowCount();
  } else {
    scheduleRows = permutationCount;
  }
  const iterMultiplier = altMode === 'random' ? N : 1;
  const totalIterations = scheduleRows * iterMultiplier;

  if (totalIterations > 500) {
    issues.push(`Total iterations (${totalIterations}) exceeds the soft cap of 500.`);
  }

  return {
    permutationCount,
    totalIterations,
    permuteQids,
    poolSizes,
    issues,
  };
}

function snapshot(): Snapshot {
  if (!state.baseImage) throw new Error('No base image');
  const expertConfig = new Map<string, ExpertQuestionConfig>();
  for (const [k, v] of state.expertConfig) {
    expertConfig.set(k, {
      questionId: v.questionId,
      mode: v.mode,
      options: v.options.map((o) => ({ ...o })),
      photoMode: v.photoMode,
      altMode: v.altMode,
    });
  }
  return {
    questions: state.questions.map((q) => deepCloneQuestion(q)),
    questionOrder: state.questions.map((q) => q.id),
    targets: state.targets.map((t) => ({ ...t, priority: [...t.priority] })),
    areas: state.areas.map((a) => ({
      ...a,
      targets: a.targets.map((t) => ({ ...t })),
      extraImages: a.extraImages.map((ei) => ({ ...ei })),
    })),
    baseImage: { ...state.baseImage },
    openingPrompt: state.openingPrompt,
    closingPrompt: state.closingPrompt,
    targetTemplate: state.targetTemplate,
    expertConfig,
    permutationMode: state.expertPermutationMode,
    altCoverageMode: state.expertAltCoverageMode,
    iterationsPerPermutation: Math.max(1, state.expertIterationsPerPermutation | 0),
    concurrency: Math.max(1, Math.min(50, state.expertConcurrency | 0)),
    reviewPerArea: state.expertReviewPerArea,
    rotateCursorByQid: new Map(),
  };
}

function deepCloneQuestion(q: Question): Question {
  if (isChoice(q)) {
    return {
      ...q,
      options: q.options.map((o) => ({
        ...o,
        prompts: o.prompts.map((p) => ({
          ...p,
          alternatives: p.alternatives.map((a) => ({ ...a })),
        })),
      })),
      selected: [...q.selected],
    };
  }
  return {
    ...q,
    prompts: q.prompts.map((p) => ({
      ...p,
      alternatives: p.alternatives.map((a) => ({ ...a })),
    })),
  };
}

function enumeratePermutations(snap: Snapshot): Array<Record<string, number>> {
  const permuteQs: Array<{ qid: string; pool: number[] }> = [];
  for (const q of snap.questions) {
    const cfg = snap.expertConfig.get(q.id);
    if (!cfg || cfg.mode !== 'permute' || !isChoice(q)) continue;
    const pool = cfg.options.map((o, i) => (o.inPool ? i : -1)).filter((i) => i >= 0);
    if (pool.length > 0) permuteQs.push({ qid: q.id, pool });
  }

  if (permuteQs.length === 0) return [{}];

  if (snap.permutationMode === 'cover') {
    const rows = Math.max(...permuteQs.map((p) => p.pool.length));
    const out: Array<Record<string, number>> = [];
    for (let r = 0; r < rows; r++) {
      const acc: Record<string, number> = {};
      for (const { qid, pool } of permuteQs) {
        acc[qid] = pool[r % pool.length];
      }
      out.push(acc);
    }
    return out;
  }

  const out: Array<Record<string, number>> = [];
  const walk = (i: number, acc: Record<string, number>) => {
    if (i === permuteQs.length) {
      out.push({ ...acc });
      return;
    }
    const { qid, pool } = permuteQs[i];
    for (const idx of pool) {
      acc[qid] = idx;
      walk(i + 1, acc);
    }
  };
  walk(0, {});
  return out;
}

function optionAltDepth(q: ChoiceQuestion, optIdx: number): number {
  const opt = q.options[optIdx];
  if (!opt || !opt.injects || opt.prompts.length === 0) return 1;
  let max = 1;
  for (const g of opt.prompts) max = Math.max(max, g.alternatives.length);
  return Math.max(1, max);
}

interface ScheduleRow {
  pins: Record<string, number>;
  altIdxByQid: Record<string, number>;
}

function depthForRow(snap: Snapshot, pins: Record<string, number>): number {
  let depth = 1;
  for (const q of snap.questions) {
    const cfg = snap.expertConfig.get(q.id);
    if (!cfg) continue;
    if (isChoice(q)) {
      let optIdx = pins[q.id];
      if (optIdx === undefined && cfg.mode === 'fixed') {
        const pool = cfg.options.map((o, i) => (o.inPool ? i : -1)).filter((i) => i >= 0);
        optIdx = pool[0];
      }
      if (optIdx === undefined) continue;
      depth = Math.max(depth, optionAltDepth(q, optIdx));
    } else if (isPhoto(q)) {
      for (const g of q.prompts) depth = Math.max(depth, g.alternatives.length);
    }
  }
  return depth;
}

function buildSchedule(snap: Snapshot): ScheduleRow[] {
  const basePerms = enumeratePermutations(snap);

  if (snap.altCoverageMode === 'random') {
    return basePerms.map((pins) => ({ pins, altIdxByQid: {} }));
  }

  if (snap.altCoverageMode === 'simple') {
    const rows: ScheduleRow[] = [];
    for (const pins of basePerms) {
      const depth = depthForRow(snap, pins);
      for (let i = 0; i < depth; i++) {
        const altIdxByQid: Record<string, number> = {};
        for (const q of snap.questions) altIdxByQid[q.id] = i;
        rows.push({ pins, altIdxByQid });
      }
    }
    return rows;
  }

  // 'smart' — per-question schedule with opt_i visited optionAltDepth(opt_i) times.
  const perQ: Array<{ qid: string; optSched: number[]; altSched: number[] }> = [];
  for (const q of snap.questions) {
    const cfg = snap.expertConfig.get(q.id);
    if (!cfg || cfg.mode !== 'permute' || !isChoice(q)) continue;
    const pool = cfg.options.map((o, i) => (o.inPool ? i : -1)).filter((i) => i >= 0);
    if (pool.length === 0) continue;
    const optSched: number[] = [];
    const altSched: number[] = [];
    for (const optIdx of pool) {
      const k = optionAltDepth(q, optIdx);
      for (let a = 0; a < k; a++) {
        optSched.push(optIdx);
        altSched.push(a);
      }
    }
    perQ.push({ qid: q.id, optSched, altSched });
  }

  if (perQ.length === 0) {
    return basePerms.map((pins) => ({ pins, altIdxByQid: {} }));
  }

  const totalRows = Math.max(...perQ.map((p) => p.optSched.length));
  const rows: ScheduleRow[] = [];
  for (let r = 0; r < totalRows; r++) {
    const pins: Record<string, number> = {};
    const altIdxByQid: Record<string, number> = {};
    for (const p of perQ) {
      const idx = r % p.optSched.length;
      pins[p.qid] = p.optSched[idx];
      altIdxByQid[p.qid] = p.altSched[idx];
    }
    rows.push({ pins, altIdxByQid });
  }
  return rows;
}

function fileNameTokensFor(
  snap: Snapshot,
  pins: Record<string, number>,
  altIdxByQid: Record<string, number>,
): string[] {
  const tokens: string[] = [];
  const includeAlts = snap.altCoverageMode !== 'random';
  for (const q of snap.questions) {
    const cfg = snap.expertConfig.get(q.id);
    if (!cfg || !isChoice(q)) continue;
    let idx: number | undefined;
    if (cfg.mode === 'permute') {
      idx = pins[q.id];
    } else if (cfg.mode === 'fixed') {
      const pool = cfg.options.map((o, i) => (o.inPool ? i : -1)).filter((i) => i >= 0);
      idx = pool[0] ?? q.selected[0] ?? 0;
    } else {
      // shuffle/rotate vary per iteration — can't represent in the filename.
      continue;
    }
    if (idx === undefined) continue;
    const optShort = cfg.options[idx]?.shortLabel?.trim();
    let token = sanitizeToken(optShort || `opt${idx}`);

    // Append an alt slug per multi-alt group for this option.
    const opt = q.options[idx];
    const fixedAltByPrompt =
      cfg.mode === 'fixed' ? cfg.options[idx]?.fixedAltByPrompt : undefined;
    const anyFixed = fixedAltByPrompt && Object.keys(fixedAltByPrompt).length > 0;
    if (opt?.injects && (includeAlts || anyFixed)) {
      opt.prompts.forEach((g, pi) => {
        if (g.alternatives.length <= 1) return;
        const pinned = fixedAltByPrompt?.[pi];
        const ai =
          pinned !== undefined
            ? pinned % g.alternatives.length
            : (altIdxByQid[q.id] ?? 0) % g.alternatives.length;
        const altShort = g.alternatives[ai]?.shortLabel?.trim();
        token += `_${sanitizeToken(altShort || `alt${ai}`)}`;
      });
    }
    tokens.push(token);
  }
  return tokens;
}

function sanitizeToken(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'opt';
}

function resolveChoiceForIteration(
  q: ChoiceQuestion,
  cfg: ExpertQuestionConfig,
  pins: Record<string, number>,
  globalIterIdx: number,
  rotateCursor: number,
): number {
  const pool = cfg.options.map((o, i) => (o.inPool ? i : -1)).filter((i) => i >= 0);
  if (pool.length === 0) return q.selected[0] ?? 0;

  if (cfg.mode === 'permute') {
    return pins[q.id] ?? pool[0];
  }
  if (cfg.mode === 'fixed') {
    return pool[0];
  }
  if (cfg.mode === 'rotate') {
    return pool[(rotateCursor + globalIterIdx) % pool.length];
  }
  // shuffle
  return pool[Math.floor(Math.random() * pool.length)];
}

async function photoFileFor(
  q: PhotoQuestion,
  cfg: ExpertQuestionConfig,
): Promise<File | null> {
  if (cfg.photoMode === 'fixed') return q.file;
  const fresh = await fetchRandomPortraitFile();
  return fresh ?? q.file;
}

interface Emission {
  qid: string;
  target: string;
  text: string;
}

function resolveEmissions(
  snap: Snapshot,
  selectionByQid: Record<string, number>,
  photoByQid: Map<string, File | null>,
  altIdxByQid: Record<string, number>,
): Emission[] {
  const emissions: Emission[] = [];
  const altCoverage = snap.altCoverageMode;
  for (const q of snap.questions) {
    const cfg = snap.expertConfig.get(q.id);
    if (!cfg) continue;

    const pushGroup = (group: AnswerPrompt, optIdx?: number, promptIdx?: number) => {
      const alts = group.alternatives;
      if (alts.length === 0) return;
      let alt;
      const fixedAltIdx =
        cfg.mode === 'fixed' && optIdx !== undefined && promptIdx !== undefined
          ? cfg.options[optIdx]?.fixedAltByPrompt?.[promptIdx]
          : undefined;
      if (fixedAltIdx !== undefined) {
        alt = alts[fixedAltIdx % alts.length];
      } else if (altCoverage !== 'random') {
        const ai = altIdxByQid[q.id] ?? 0;
        alt = alts[ai % alts.length];
      } else if (cfg.altMode === 'cycle') {
        const idx = 0; // cycle offset is session-local; expert mode picks shuffle by default
        alt = alts[idx];
      } else {
        alt = pickOne(alts);
      }
      if (!alt) return;
      const target = alt.target.trim();
      const text = alt.text.trim();
      if (!target || !text) return;
      emissions.push({ qid: q.id, target, text });
    };

    if (isChoice(q)) {
      const idx = selectionByQid[q.id];
      if (idx === undefined) continue;
      const opt = q.options[idx];
      if (!opt || !opt.injects) continue;
      opt.prompts.forEach((group, promptIdx) => pushGroup(group, idx, promptIdx));
    } else if (isPhoto(q)) {
      const file = photoByQid.get(q.id);
      if (!file) continue;
      for (const group of q.prompts) pushGroup(group);
    }
  }
  return emissions;
}

function composeAreaPrompt(area: Area, emissions: Emission[], snap: Snapshot): string {
  const parts: string[] = [];
  if (snap.openingPrompt.trim()) parts.push(snap.openingPrompt.trim());

  for (const at of area.targets) {
    const t = snap.targets.find((x) => x.id === at.targetId);
    if (!t) continue;
    const candidates = emissions.filter((e) => e.target === t.name);
    if (candidates.length === 0) continue;

    const priorityTail = snap.questionOrder.filter((id) => !t.priority.includes(id));
    const order = [...t.priority, ...priorityTail];
    let winnerQid: string | null = null;
    for (const qid of order) {
      if (candidates.some((c) => c.qid === qid)) {
        winnerQid = qid;
        break;
      }
    }
    if (!winnerQid) continue;

    const finalists = candidates.filter((c) => c.qid === winnerQid);
    const chosen = pickOne(finalists);
    if (!chosen) continue;

    const description = at.description.trim();
    if (description) {
      parts.push(
        snap.targetTemplate
          .replaceAll('[DESCRIPTION]', description)
          .replaceAll('[UPDATE]', chosen.text),
      );
    } else {
      parts.push(chosen.text);
    }
  }

  if (snap.closingPrompt.trim()) parts.push(snap.closingPrompt.trim());
  return parts.join('\n\n');
}

function buildSelectionChips(
  snap: Snapshot,
  selectionByQid: Record<string, number>,
): ExpertSelectionChip[] {
  const out: ExpertSelectionChip[] = [];
  for (const q of snap.questions) {
    const cfg = snap.expertConfig.get(q.id);
    if (!cfg) continue;
    if (isChoice(q)) {
      const idx = selectionByQid[q.id];
      if (idx === undefined) continue;
      const opt = q.options[idx];
      const cfgOpt = cfg.options[idx];
      out.push({
        qid: q.id,
        questionTitle: q.title,
        optionLabel: opt?.label ?? null,
        shortLabel: cfgOpt?.shortLabel ?? null,
        mode: cfg.mode,
        colorIndex: q.colorIndex,
      });
    } else if (isPhoto(q)) {
      out.push({
        qid: q.id,
        questionTitle: q.title,
        optionLabel: cfg.photoMode === 'random' ? '(random portrait)' : '(fixed photo)',
        shortLabel: null,
        mode: cfg.mode,
        colorIndex: 'photo',
      });
    }
  }
  return out;
}

function cropCanvas(
  source: HTMLCanvasElement,
  x: number,
  y: number,
  w: number,
  h: number,
  grayscale: boolean,
): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  if (grayscale) ctx.filter = 'grayscale(100%)';
  ctx.drawImage(source, x, y, w, h, 0, 0, w, h);
  return c;
}

function canvasToInline(canvas: HTMLCanvasElement): { mimeType: string; data: string } {
  const dataUrl = canvas.toDataURL('image/png');
  return dataUrlToInline(dataUrl);
}

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) throw new Error('Invalid data URL');
  const bin = atob(dataUrl.slice(comma + 1));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function callGenerateApi(
  prompt: string,
  images: Array<{ mimeType: string; data: string }>,
  imageSize: string,
  aspectRatio: string,
  onAttempt: (attempt: number) => void,
): Promise<{ imageBase64: string; mimeType: string }> {
  let lastErr = 'Unknown error';
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    onAttempt(attempt);
    try {
      const res = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ prompt, images, imageSize, aspectRatio }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { error?: string };
          if (body?.error) detail += `: ${body.error}`;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      return (await res.json()) as { imageBase64: string; mimeType: string };
    } catch (err) {
      lastErr = (err as Error).message || String(err);
    }
  }
  throw new Error(`Failed after ${MAX_ATTEMPTS} attempts: ${lastErr}`);
}

async function runOne(result: ExpertResult, snap: Snapshot, globalIterIdx: number): Promise<void> {
  result.status = 'running';
  result.startedAt = Date.now();
  result.errorMessage = undefined;
  result.dataUrl = undefined;
  result.pngBytes = undefined;
  result.areasDone = 0;
  result.areasTotal = snap.areas.length;
  result.areaStatuses = snap.areas.map(() => 'queued');
  result.currentAreaIdx = null;
  notify();

  try {
    // Build this iteration's selections.
    const selectionByQid: Record<string, number> = {};
    const photoByQid = new Map<string, File | null>();

    for (const q of snap.questions) {
      const cfg = snap.expertConfig.get(q.id);
      if (!cfg) continue;
      if (isChoice(q)) {
        const cursor = snap.rotateCursorByQid.get(q.id) ?? 0;
        selectionByQid[q.id] = resolveChoiceForIteration(
          q,
          cfg,
          result.permutePins,
          globalIterIdx,
          cursor,
        );
      } else if (isPhoto(q)) {
        photoByQid.set(q.id, await photoFileFor(q, cfg));
      }
    }

    result.selections = buildSelectionChips(snap, selectionByQid);

    const emissions = resolveEmissions(
      snap,
      selectionByQid,
      photoByQid,
      result.altIdxByQid,
    );

    // Compose the base image and fill each area.
    const baseImg = await loadImage(snap.baseImage.dataUrl);
    const work = document.createElement('canvas');
    work.width = snap.baseImage.width;
    work.height = snap.baseImage.height;
    const ctx = work.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D unavailable');
    ctx.drawImage(baseImg, 0, 0);

    const perAreaPrompts: string[] = [];
    let firstPhotoFile: File | null = null;
    for (const [, v] of photoByQid) {
      if (v) {
        firstPhotoFile = v;
        break;
      }
    }
    const photoGrayDataUrl = firstPhotoFile
      ? await fileToGrayscaleDataUrl(firstPhotoFile)
      : null;

    // Seed the preview with the base image so the viewer shows something immediately.
    result.dataUrl = work.toDataURL('image/png');
    result.mimeType = 'image/png';
    notify();

    const bailIfCanceled = (): boolean => {
      return (result.status as ExpertStatus) === 'rejected' || !expertSession.running;
    };

    for (let i = 0; i < snap.areas.length; i++) {
      if (bailIfCanceled()) {
        result.currentAreaIdx = null;
        result.durationMs = Date.now() - (result.startedAt ?? Date.now());
        result.perAreaPrompts = perAreaPrompts;
        notify();
        return;
      }
      const area = snap.areas[i];
      const { w, h } = areaDims(area);
      const prompt = composeAreaPrompt(area, emissions, snap);
      perAreaPrompts.push(prompt);
      if (!prompt.trim()) {
        result.areaStatuses[i] = 'approved';
        continue;
      }

      // Snapshot this area's pixels before we overwrite, so a rejected regen can
      // start from the same input as the original attempt.
      const preArea = ctx.getImageData(area.x, area.y, w, h);

      let regenerate = false;
      do {
        regenerate = false;
        result.currentAreaIdx = i;
        result.areaStatuses[i] = 'running';
        notify();

        // Restore pre-area pixels in case this is a regen pass.
        ctx.putImageData(preArea, area.x, area.y);

        const cropEl = cropCanvas(work, area.x, area.y, w, h, true);
        const images = [canvasToInline(cropEl)];
        if (area.includeBase) {
          const baseCanvasEl = cropCanvas(
            work,
            0,
            0,
            snap.baseImage.width,
            snap.baseImage.height,
            true,
          );
          images.push(canvasToInline(baseCanvasEl));
        }
        for (const ei of area.extraImages) {
          images.push(dataUrlToInline(ei.dataUrl));
        }
        if (area.includePhoto && photoGrayDataUrl) {
          images.push(dataUrlToInline(photoGrayDataUrl));
        }

        const out = await callGenerateApi(
          prompt,
          images,
          area.imageSize,
          area.aspectRatio,
          (attempt) => {
            result.attempts = attempt;
            notify();
          },
        );

        if (bailIfCanceled()) {
          result.currentAreaIdx = null;
          result.durationMs = Date.now() - (result.startedAt ?? Date.now());
          result.perAreaPrompts = perAreaPrompts;
          notify();
          return;
        }

        const resultImg = await loadImage(`data:${out.mimeType};base64,${out.imageBase64}`);
        ctx.drawImage(resultImg, area.x, area.y, w, h);

        // Publish a live preview snapshot after each area lands.
        result.dataUrl = work.toDataURL('image/png');
        notify();

        if (snap.reviewPerArea) {
          result.areaStatuses[i] = 'awaiting';
          notify();
          const decision = await new Promise<AreaDecision>((resolve) => {
            result.__areaDecider = resolve;
          });
          result.__areaDecider = undefined;

          if (decision === 'abort' || bailIfCanceled()) {
            result.currentAreaIdx = null;
            result.durationMs = Date.now() - (result.startedAt ?? Date.now());
            result.perAreaPrompts = perAreaPrompts;
            notify();
            return;
          }
          if (decision === 'regen') {
            regenerate = true;
            continue;
          }
          // approve
          result.areaStatuses[i] = 'approved';
        } else {
          result.areaStatuses[i] = 'approved';
        }
      } while (regenerate);

      result.areasDone = i + 1;
      notify();
    }

    result.currentAreaIdx = null;
    const finalDataUrl = work.toDataURL('image/png');
    result.dataUrl = finalDataUrl;
    result.mimeType = 'image/png';
    result.pngBytes = dataUrlToBytes(finalDataUrl);
    result.perAreaPrompts = perAreaPrompts;
    if ((result.status as ExpertStatus) !== 'rejected') {
      // Per-area review already had the user approve each area; in that mode
      // the whole result is implicitly approved.
      result.status = snap.reviewPerArea ? 'approved' : 'pending';
    }
    result.durationMs = Date.now() - (result.startedAt ?? Date.now());
  } catch (err) {
    if ((result.status as ExpertStatus) !== 'rejected') {
      result.status = 'error';
      result.errorMessage = (err as Error).message || String(err);
    }
    result.durationMs = Date.now() - (result.startedAt ?? Date.now());
  }
  notify();
}

let activeWorkers = 0;

function claimNextQueued(): { result: ExpertResult; index: number } | null {
  for (let i = 0; i < expertSession.results.length; i++) {
    const r = expertSession.results[i];
    if (r.status === 'queued') {
      r.status = 'running';
      return { result: r, index: i };
    }
  }
  return null;
}

function pump(snap: Snapshot): void {
  const limit = Math.max(1, Math.min(50, state.expertConcurrency | 0));
  while (activeWorkers < limit) {
    const claimed = claimNextQueued();
    if (!claimed) break;
    spawnWorker(snap, claimed.result, claimed.index);
  }
  maybeFinish();
}

function spawnWorker(snap: Snapshot, initial: ExpertResult, initialIndex: number): void {
  activeWorkers++;
  void (async () => {
    try {
      await runOne(initial, snap, initialIndex);
      while (expertSession.running) {
        const next = claimNextQueued();
        if (!next) break;
        await runOne(next.result, snap, next.index);
      }
    } finally {
      activeWorkers--;
      maybeFinish();
      notify();
    }
  })();
}

function maybeFinish(): void {
  if (!expertSession.running) return;
  const hasPending = expertSession.results.some(
    (r) => r.status === 'queued' || r.status === 'running',
  );
  if (!hasPending && activeWorkers === 0) {
    expertSession.running = false;
    notify();
  }
}

export async function startExpertBatch(): Promise<void> {
  if (expertSession.running) return;

  const preview = computeExpertPreview();
  if (preview.issues.length > 0) {
    throw new Error(preview.issues.join('\n'));
  }

  const snap = snapshot();
  expertSession.snapshot = snap;
  expertSession.running = true;
  expertSession.startedAt = Date.now();
  expertSession.results = [];
  notify();

  const schedule = buildSchedule(snap);
  const results: ExpertResult[] = [];

  const iterMultiplier =
    snap.altCoverageMode === 'random' ? snap.iterationsPerPermutation : 1;
  const multi = iterMultiplier > 1;
  const fileNameCounts = new Map<string, number>();

  schedule.forEach((row, permIdx) => {
    const tokens = fileNameTokensFor(snap, row.pins, row.altIdxByQid);
    const base = tokens.filter(Boolean).join('-') || 'image';
    for (let c = 0; c < iterMultiplier; c++) {
      // If two rows produce the same tokens (e.g., smart mode repeats an option), disambiguate.
      const seen = fileNameCounts.get(base) ?? 0;
      fileNameCounts.set(base, seen + 1);
      const suffix = multi ? `-${c}` : '';
      let name = `${base}${suffix}.png`;
      if (seen > 0) name = `${base}${suffix}-${seen}.png`;
      results.push({
        id: crypto.randomUUID(),
        fileName: name,
        permutationIndex: permIdx,
        copyIndex: c,
        status: 'queued',
        selections: [],
        perAreaPrompts: [],
        permutePins: { ...row.pins },
        altIdxByQid: { ...row.altIdxByQid },
        attempts: 0,
        areasDone: 0,
        areasTotal: snap.areas.length,
        areaStatuses: snap.areas.map(() => 'queued'),
        currentAreaIdx: null,
      });
    }
  });

  expertSession.results = results;
  notify();

  pump(snap);
}

export async function regenerateRejectedAndErrors(): Promise<void> {
  const snap = expertSession.snapshot;
  if (!snap) return;

  const targets = expertSession.results.filter(
    (r) => r.status === 'rejected' || r.status === 'error',
  );
  if (targets.length === 0) return;

  for (const r of targets) {
    r.status = 'queued';
    r.errorMessage = undefined;
    r.dataUrl = undefined;
    r.pngBytes = undefined;
    r.attempts = 0;
    r.durationMs = undefined;
    r.areasDone = 0;
    r.areaStatuses = r.areaStatuses.map(() => 'queued');
    r.currentAreaIdx = null;
  }

  if (!expertSession.running) {
    expertSession.running = true;
    expertSession.startedAt = Date.now();
  }
  notify();
  pump(snap);
}

export function stopExpertBatch(): void {
  if (!expertSession.running) return;
  expertSession.running = false;
  notify();
}

export function setResultStatus(id: string, status: ExpertStatus): void {
  const r = expertSession.results.find((x) => x.id === id);
  if (!r) return;
  // Allow rejecting a running iteration to cancel it mid-flight; otherwise queued/running are read-only.
  if (r.status === 'queued') return;
  if (r.status === 'running' && status !== 'rejected') return;
  if (status === 'approved' || status === 'rejected' || status === 'pending') {
    r.status = status;
    // If this result was paused on per-area review, unblock it.
    if (r.__areaDecider) {
      const resolve = r.__areaDecider;
      r.__areaDecider = undefined;
      resolve('abort');
    }
    notify();
  }
}

export function approveCurrentArea(id: string): void {
  const r = expertSession.results.find((x) => x.id === id);
  if (!r || !r.__areaDecider) return;
  const resolve = r.__areaDecider;
  r.__areaDecider = undefined;
  resolve('approve');
}

export function regenerateCurrentArea(id: string): void {
  const r = expertSession.results.find((x) => x.id === id);
  if (!r || !r.__areaDecider) return;
  if (r.currentAreaIdx !== null) {
    r.areaStatuses[r.currentAreaIdx] = 'rejected';
  }
  const resolve = r.__areaDecider;
  r.__areaDecider = undefined;
  resolve('regen');
}

export interface ZipBuildOptions {
  includeUnreviewed: boolean;
}

export interface ZipBuildResult {
  blob: Blob;
  filename: string;
  included: number;
  excluded: number;
}

export function buildExpertZip(opts: ZipBuildOptions): ZipBuildResult {
  const include = (r: ExpertResult): boolean => {
    if (r.status === 'approved') return true;
    if (opts.includeUnreviewed && r.status === 'pending') return true;
    return false;
  };
  const entries: ZipEntry[] = [];
  let included = 0;
  let excluded = 0;
  for (const r of expertSession.results) {
    if (!include(r) || !r.pngBytes) {
      if (!include(r)) excluded++;
      continue;
    }
    entries.push({ path: r.fileName, data: r.pngBytes });
    included++;
  }

  const blob = makeZip(entries);
  const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
  const filename = `expert-${stamp}.zip`;
  return { blob, filename, included, excluded };
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
